import { Tkv2OrderAttachment, roundTo } from '@aloft/toolkit-shared';
import { ProductColor } from '@aloft/toolkit-sui';
import { UseQueryResult } from '@tanstack/react-query';

import { useGetPropertyImageAnalysis } from '../../../../hooks/api/schema-hooks/useGetPropertyImageAnalysis.ts';
import { selectOrder } from '../../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import { AnalysisLabel, AnalysisLabelProps } from './AnalysisLabel.tsx';

export const ImageAnalysisQuality = ({
  id,
  step,
  color,
}: {
  id: string;
  step: number;
  color?: ProductColor;
}) => {
  const orderID = useAppSelector(selectOrder).id;
  const analysis = useGetPropertyImageAnalysis(
    { pathParams: { orderID, propertyID: id === 'subjectArv' ? 'subject' : id } },
    { retry: false, enabled: !!orderID, staleTime: Infinity },
  );

  if (id === 'subjectArv') return null;
  const analysisState = getAnalysisState(analysis, step);

  return <AnalysisLabel {...analysisState} color={color} />;
};

const getAnalysisState = (
  analysis: UseQueryResult<{ content: Tkv2OrderAttachment<'imageAnalysis'> }>,
  step: number,
): AnalysisLabelProps => {
  const recommend = analysis.data?.content.body.result.overall_class;
  const recommendRounded = recommend === undefined ? undefined : roundTo(recommend, step);

  if (analysis.isLoading) {
    return {
      state: 'loading',
      tooltip: 'Running AI image analysis to determine a recommended cost quality',
    };
  }
  if (analysis.isError || recommendRounded === undefined) {
    return {
      state: 'error',
      tooltip: 'Unable to run AI image analysis for cost quality',
    };
  }

  return {
    state: 'success',
    tooltip: `AI image analysis recommends a cost quality of ${recommendRounded.toFixed(1)}`,
    recommend: recommendRounded.toFixed(1),
  };
};
